/**
 * Subprocessor — detail / editor. Tracks one vendor's GDPR obligations: contract status,
 * data types processed, international transfer mechanism, DPA status.
 *
 * Routes
 *   /subprocessors/new              mints an empty draft server-side then redirects, so there
 *                                    is never an "unsaved new record" state.
 *   /subprocessors/:subprocessorId  the editor for that record.
 *
 * A single editable document, six section cards, matching Processing Activities' own
 * convention rather than a wizard - this too gets filled in by different people at different
 * times (legal reviews the contract terms, the DPO reviews the transfer mechanism).
 *
 * `dataFields` deliberately reuses Processing Activities' exact repeatable-row shape and
 * editor pattern (RepeatableRowCard, category/sensitivity/isPersonalData/isSpecialCategory) -
 * see the register's own module docstring for why. The one addition here is a suggestion
 * datalist for "what data" (PERSONAL_DATA_TYPE_SUGGESTIONS), which Processing Activities
 * doesn't have yet - picking a Special Category suggestion pre-checks isSpecialCategory,
 * matching the same reset-on-clear discipline ROPA's editor already uses.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Select from 'react-select';

import { PageHeader } from '../../components/PageHeader';
import { newRowId, useRepeatableRows, RepeatableRowCard } from '../../components/RepeatableRows';
import type { RowAccent } from '../../components/RepeatableRows';
import { DocumentManager } from '../../components/DocumentManager';
import { LinkedArtefacts } from '../../components/LinkedArtefacts';

import { subprocessorsApi, peopleApi, assetsApi } from '../../services/api';
import type { DataField, Person } from '../../types/models';
import {
  DATA_CATEGORIES, DATA_CATEGORY_LABELS,
  SENSITIVITY_LEVELS, SENSITIVITY_LABELS,
  DATA_SENSITIVITY_ANSWERS, DATA_SENSITIVITY_LABELS, DATA_SENSITIVITY_TONE, DATA_SENSITIVITY_HINT,
  TRANSFER_MECHANISMS, TRANSFER_MECHANISM_LABELS, TRANSFER_MECHANISM_TONE,
  PERSONAL_DATA_TYPE_SUGGESTIONS, isSpecialCategoryDataType,
} from '../../constants/privacy';
import { reactSelectStyles } from '../../utils/reactSelectStyles';
import { formatDate } from '../../utils/format';
import { describeApiError } from '../../utils/apiError';
import { useToast } from '../../hooks/useToast';
import { useSwal } from '../../hooks/useSwal';

interface SelectOption { value: string; label: string }

interface Asset { assetId: string; name: string }

export interface Subprocessor {
  subprocessorId: string;
  tenantId: string;
  name: string;
  product: string;
  systemIds: string[];
  controllerOrProcessor: 'Controller' | 'Processor';
  vendorRole: VendorRole[];
  ownerId: string;
  contractType: string;
  contractDuration: 'Fixed term' | 'Ongoing' | 'Unknown';
  contractEndDate: string;
  autoExtension: 'Yes' | 'No' | 'Not applicable';
  extensionPeriod: string;
  noticePeriod: string;
  contractTermsUrl: string;
  dataFields: DataField[];
  includesCustomerData: boolean;
  includesEmployeeData: boolean;
  locationOfProcessing: string;
  descriptionOfProcessing: string;
  transfersOutsideEea: boolean;
  transferMechanism: string;
  transferDestination: string;
  transferPreApproved: boolean;
  dpaInPlace: 'Yes' | 'No' | 'Not applicable';
  dpaUrl: string;
  status: 'Draft' | 'Active' | 'Terminated';
  lastReviewDate: string;
  nextReviewDate: string;
  notes: string;
  // System-owned: set once at creation by the approval-time drafting flow when this record
  // was auto-drafted from an AI Use Case's coverage check. '' for any manually-created subprocessor.
  sourceUseCaseId: string;
  createdAt: string;
  updatedAt: string;
}

const CONTRACT_TYPES = [
  'Custom contract', 'Standard subscription', 'Statement of work',
  'ASP agreement', 'Ad-hoc usage', 'Free service', 'Other',
];
const CONTRACT_DURATIONS = ['Fixed term', 'Ongoing', 'Unknown'] as const;
const TRI_STATE = ['Yes', 'No', 'Not applicable'] as const;
const STATUSES = ['Draft', 'Active', 'Terminated'] as const;

// The vendor's own role(s) on this relationship - distinct from `controllerOrProcessor` above
// ("Mercell is"), which is Mercell's own role, not the vendor's. A vendor can genuinely be more
// than one of these at once (e.g. a platform that is Mercell's processor for one data flow and
// an independent controller for another), hence an array rather than the old 2-value single-select.
export const VENDOR_ROLES = ['Processor', 'Subprocessor', 'Controller'] as const;
export type VendorRole = typeof VENDOR_ROLES[number];
export const VENDOR_ROLE_LABELS: Record<VendorRole, string> = {
  Processor: 'Processor', Subprocessor: 'Subprocessor', Controller: 'Controller',
};
export const VENDOR_ROLE_TONE: Record<VendorRole, string> = {
  Processor: 'info', Subprocessor: 'neutral', Controller: 'warning',
};
const VENDOR_ROLE_OPTIONS: SelectOption[] = VENDOR_ROLES.map((v) => ({ value: v, label: VENDOR_ROLE_LABELS[v] }));

/** De-duplicated, filtered to the closed vocabulary - tolerates a legacy scalar value (every
 * record created before this field became multi-select stored one bare string) by wrapping it
 * in a single-item array rather than dropping it. Mirrors the backend's own normalise_vendor_roles,
 * so a never-saved legacy record still displays correctly here even before its next save. */
export function toVendorRoles(v: unknown): VendorRole[] {
  const raw: unknown[] = Array.isArray(v) ? v : v ? [v] : [];
  const out: VendorRole[] = [];
  for (const entry of raw) {
    const value = String(entry) as VendorRole;
    if ((VENDOR_ROLES as readonly string[]).includes(value) && !out.includes(value)) out.push(value);
  }
  return out;
}

const Spinner: React.FC = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

const SectionCard: React.FC<{
  title: string;
  icon: string;
  hint?: string;
  children: React.ReactNode;
}> = ({ title, icon, hint, children }) => (
  <div className="np-card">
    <div className="np-card-head">
      <span className="np-icon-badge np-icon-badge-sm np-tone-primary"><i className={icon}></i></span>
      <div>
        <h2 className="mb-0 fw-semibold">{title}</h2>
        {hint && <div className="np-sub">{hint}</div>}
      </div>
    </div>
    <div className="np-card-body">{children}</div>
  </div>
);

const STATUS_TONE: Record<string, string> = { Draft: 'neutral', Active: 'success', Terminated: 'dark' };

// ─── Editor ───────────────────────────────────────────────────────────────────

const SubprocessorEditor: React.FC<{ record: Subprocessor }> = ({ record }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const swal = useSwal();

  // Lazy initializer, not a plain value: this normalizes a legacy scalar vendorRole into an
  // array ONLY for the initial mount, so opening an un-migrated record never marks it dirty.
  const [draft, setDraft] = useState<Subprocessor>(() => ({ ...record, vendorRole: toVendorRoles(record.vendorRole) }));
  const [dirty, setDirty] = useState(false);

  const update = useCallback((changes: Partial<Subprocessor>) => {
    setDraft((prev) => ({ ...prev, ...changes }));
    setDirty(true);
  }, []);

  const { data: people = [] } = useQuery<Person[]>({ queryKey: ['people'], queryFn: peopleApi.getAll });
  const { data: assets = [] } = useQuery<Asset[]>({ queryKey: ['assets'], queryFn: assetsApi.getAll });

  const peopleOptions: SelectOption[] = useMemo(
    () => people.map((p) => ({ value: p.personId, label: p.personName })), [people],
  );
  const assetOptions: SelectOption[] = useMemo(
    () => assets.map((a) => ({ value: a.assetId, label: a.name })), [assets],
  );

  const optionFor = (options: SelectOption[], id: string): SelectOption | null => {
    if (!id) return null;
    return options.find((o) => o.value === id) ?? { value: id, label: id };
  };
  const selectedMulti = (options: SelectOption[], ids: string[]): SelectOption[] =>
    ids.map((id) => options.find((o) => o.value === id) ?? { value: id, label: id });

  const dataRows = useRepeatableRows<DataField>({
    stored: draft.dataFields,
    makeBlank: () => ({
      id: newRowId(), name: '', category: 'personal',
      isPersonalData: 'yes', isSpecialCategory: false,
      sensitivity: 'internal', processingPurpose: '', dataSource: '',
    }),
    isBlank: (row) => !row.name.trim() && !row.processingPurpose.trim(),
    onChange: (rows) => update({ dataFields: rows }),
  });

  const writablePayload = (d: Subprocessor) => ({
    name: d.name, product: d.product, systemIds: d.systemIds,
    controllerOrProcessor: d.controllerOrProcessor, vendorRole: d.vendorRole, ownerId: d.ownerId,
    contractType: d.contractType, contractDuration: d.contractDuration, contractEndDate: d.contractEndDate,
    autoExtension: d.autoExtension, extensionPeriod: d.extensionPeriod, noticePeriod: d.noticePeriod,
    contractTermsUrl: d.contractTermsUrl,
    dataFields: d.dataFields, includesCustomerData: d.includesCustomerData, includesEmployeeData: d.includesEmployeeData,
    locationOfProcessing: d.locationOfProcessing, descriptionOfProcessing: d.descriptionOfProcessing,
    transfersOutsideEea: d.transfersOutsideEea, transferMechanism: d.transferMechanism,
    transferDestination: d.transferDestination, transferPreApproved: d.transferPreApproved,
    dpaInPlace: d.dpaInPlace, dpaUrl: d.dpaUrl,
    status: d.status, lastReviewDate: d.lastReviewDate, nextReviewDate: d.nextReviewDate, notes: d.notes,
  });

  const saveMutation = useMutation({
    mutationFn: () => subprocessorsApi.update(draft.subprocessorId, writablePayload(draft)),
    onSuccess: (saved) => {
      setDraft({ ...saved, vendorRole: toVendorRoles(saved.vendorRole) });
      setDirty(false);
      queryClient.setQueryData(['subprocessor', saved.subprocessorId], saved);
      queryClient.invalidateQueries({ queryKey: ['list', '/subprocessors'] });
      toast.success('Record updated.');
    },
    onError: (err) => toast.error(describeApiError(err, 'Could not save this record.')),
  });

  const deleteMutation = useMutation({
    mutationFn: () => subprocessorsApi.delete(draft.subprocessorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['list', '/subprocessors'] });
      toast.success('Record deleted.');
      navigate('/subprocessors');
    },
    onError: (err) => toast.error(describeApiError(err, 'Could not delete this record.')),
  });

  const handleDelete = async () => {
    const ok = await swal.confirmDelete(draft.name || draft.subprocessorId);
    if (ok) deleteMutation.mutate();
  };

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-shield-keyhole-line"
        title={draft.name || draft.subprocessorId}
        subtitle={`${draft.subprocessorId} · updated ${formatDate(draft.updatedAt)}`}
        colour={STATUS_TONE[draft.status]}
        breadcrumbs={[
          { label: 'Controllers & Processors', to: '/subprocessors' },
          { label: draft.subprocessorId },
        ]}
        actions={
          <>
            {/* A plain 3-value enum, not a gated state machine (no transition endpoint exists
                server-side - any value is PUT-able at any time, staged into `draft` and only
                persisted on Save like every other field) - so this stays a select rather than
                becoming AiUseCaseDetail.tsx-style per-transition buttons. It was buried inside
                the Review & Notes card; moved up next to Save since it's the one field people
                actually come here to change. */}
            <select
              className="np-select"
              style={{ width: 'auto' }}
              value={draft.status}
              onChange={(e) => update({ status: e.target.value as Subprocessor['status'] })}
              aria-label="Status"
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button
              className="np-btn np-btn-primary"
              onClick={() => saveMutation.mutate()}
              disabled={!dirty || saveMutation.isPending}
            >
              <i className="ri-save-line"></i>
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
            <button className="np-icon-btn np-icon-btn-danger" title="Delete" onClick={handleDelete}>
              <i className="ri-delete-bin-line"></i>
            </button>
            <button className="np-btn np-btn-ghost" onClick={() => navigate('/subprocessors')}>
              <i className="ri-arrow-left-line"></i>Back
            </button>
          </>
        }
      />

      {draft.sourceUseCaseId && (
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Link to={`/ai-use-cases/${draft.sourceUseCaseId}`} className="np-chip np-tone-info">
            <i className="ri-magic-line"></i>Drafted from {draft.sourceUseCaseId}
          </Link>
        </div>
      )}

      {/* Readiness checklist - only for an AI-drafted record still in Draft, since these are
          exactly the fields _draft_subprocessor_attrs (lambda/prod-nimbus-ai-use-cases) never
          sets at all: contractType and dpaInPlace stay at their default_attributes() placeholder
          ('Custom contract' / 'Not applicable') forever, and contractTermsUrl/dpaUrl stay blank.
          A manually-created record has no sourceUseCaseId and never shows this - a human who
          typed those defaults chose them deliberately. */}
      {draft.sourceUseCaseId && draft.status === 'Draft' && (() => {
        const items: string[] = [];
        if (draft.contractType === 'Custom contract') items.push('Contract type has not been reviewed');
        if (!draft.contractTermsUrl) items.push('Contract terms URL has not been set');
        if (draft.dpaInPlace === 'Not applicable') items.push('Whether a DPA is in place has not been reviewed');
        if (!draft.dpaUrl) items.push('DPA URL has not been set');
        return items.length > 0 ? (
          <div className="np-banner np-tone-warning mb-0">
            <i className="ri-list-check-3"></i>
            <div>
              <strong>Needs attention before this leaves draft</strong>
              <ul className="mb-0">
                {items.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </div>
          </div>
        ) : null;
      })()}

      <SectionCard title="Identity & role" icon="ri-building-4-line">
        <div className="np-row2">
          <div className="np-field">
            <label>Vendor name</label>
            <input className="np-input" value={draft.name} onChange={(e) => update({ name: e.target.value })} />
          </div>
          <div className="np-field">
            <label>Product</label>
            <input className="np-input" value={draft.product} onChange={(e) => update({ product: e.target.value })} />
          </div>
        </div>
        <div className="np-field">
          <label>Systems / platforms supported</label>
          <Select<SelectOption, true>
            isMulti isClearable isSearchable
            menuPortalTarget={document.body}
            styles={reactSelectStyles()}
            options={assetOptions}
            value={selectedMulti(assetOptions, draft.systemIds)}
            onChange={(selected) => update({ systemIds: (selected || []).map((s) => s.value) })}
            placeholder="Select the systems this vendor supports…"
          />
        </div>
        <div className="np-row2">
          <div className="np-field">
            <label>Mercell is</label>
            <select className="np-select" value={draft.controllerOrProcessor}
              onChange={(e) => update({ controllerOrProcessor: e.target.value as Subprocessor['controllerOrProcessor'] })}>
              <option value="Controller">Controller</option>
              <option value="Processor">Processor</option>
            </select>
          </div>
          <div className="np-field">
            <label htmlFor="sp-vendor-role">Vendor's role</label>
            <Select<SelectOption, true>
              inputId="sp-vendor-role"
              isMulti isClearable isSearchable
              menuPortalTarget={document.body}
              styles={reactSelectStyles()}
              options={VENDOR_ROLE_OPTIONS}
              value={selectedMulti(VENDOR_ROLE_OPTIONS, draft.vendorRole)}
              onChange={(selected) => update({ vendorRole: (selected || []).map((s) => s.value as VendorRole) })}
              placeholder="Select the vendor's role(s)…"
            />
            <p className="np-field-caption mb-0">The vendor's own role(s) - distinct from "Mercell is" above.</p>
          </div>
        </div>
        <div className="np-field">
          <label>Responsible at Mercell</label>
          <Select<SelectOption>
            isClearable isSearchable
            menuPortalTarget={document.body}
            styles={reactSelectStyles()}
            options={peopleOptions}
            value={optionFor(peopleOptions, draft.ownerId)}
            onChange={(selected) => update({ ownerId: selected?.value || '' })}
            placeholder="Select an owner…"
          />
        </div>
      </SectionCard>

      <SectionCard title="Contract" icon="ri-file-list-3-line">
        <div className="np-row2">
          <div className="np-field">
            <label>Contract type</label>
            <select className="np-select" value={draft.contractType} onChange={(e) => update({ contractType: e.target.value })}>
              {CONTRACT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="np-field">
            <label>Duration</label>
            <select className="np-select" value={draft.contractDuration}
              onChange={(e) => update({ contractDuration: e.target.value as Subprocessor['contractDuration'] })}>
              {CONTRACT_DURATIONS.map((d) => <option key={d} value={d}>{d === 'Ongoing' ? 'Ongoing (no fixed end)' : d}</option>)}
            </select>
          </div>
        </div>
        {draft.contractDuration === 'Fixed term' && (
          <div className="np-field">
            <label>Contract end date</label>
            <input type="date" className="np-input" value={draft.contractEndDate}
              onChange={(e) => update({ contractEndDate: e.target.value })} />
          </div>
        )}
        <div className="np-row2">
          <div className="np-field">
            <label>Auto extension</label>
            <select className="np-select" value={draft.autoExtension}
              onChange={(e) => update({ autoExtension: e.target.value as Subprocessor['autoExtension'] })}>
              {TRI_STATE.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div className="np-field">
            <label>Extension period</label>
            <input className="np-input" placeholder="e.g. 12 months" value={draft.extensionPeriod}
              onChange={(e) => update({ extensionPeriod: e.target.value })} />
          </div>
        </div>
        <div className="np-row2">
          <div className="np-field">
            <label>Notice period</label>
            <input className="np-input" placeholder="e.g. 30 days" value={draft.noticePeriod}
              onChange={(e) => update({ noticePeriod: e.target.value })} />
          </div>
          <div className="np-field">
            <label>Contract terms (URL)</label>
            <input type="url" className="np-input" value={draft.contractTermsUrl}
              onChange={(e) => update({ contractTermsUrl: e.target.value })} />
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Data processing" icon="ri-database-2-line" hint="What personal data this vendor processes">
        {dataRows.rows.map((row, index) => (
          <RepeatableRowCard
            key={row.id}
            label="Data type"
            index={index}
            accent={(
              row.isSpecialCategory ? 'danger'
                : row.isPersonalData !== 'no' ? (DATA_SENSITIVITY_TONE[row.isPersonalData] as RowAccent)
                  : undefined
            )}
            chips={
              <>
                {row.isPersonalData !== 'no' && (
                  <span className={`np-chip np-tone-${DATA_SENSITIVITY_TONE[row.isPersonalData]}`}>
                    {row.isPersonalData === 'yes' ? 'Personal data' : 'Personal data?'}
                  </span>
                )}
                {row.isSpecialCategory && (
                  <span className="np-chip np-tone-danger"><i className="ri-alert-line"></i>Article 9</span>
                )}
              </>
            }
            onRemove={dataRows.canRemove ? () => dataRows.remove(row.id) : undefined}
          >
            <div className="np-row2">
              <div className="np-field">
                <label>What data</label>
                <input
                  className="np-input"
                  list={`sub-data-type-list-${row.id}`}
                  value={row.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    dataRows.patch(row.id, isSpecialCategoryDataType(name)
                      ? { name, isSpecialCategory: true }
                      : { name });
                  }}
                  placeholder="e.g. Email address"
                />
                <datalist id={`sub-data-type-list-${row.id}`}>
                  {PERSONAL_DATA_TYPE_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
                </datalist>
              </div>
              <div className="np-field">
                <label>Category</label>
                <select className="np-select" value={row.category}
                  onChange={(e) => dataRows.patch(row.id, { category: e.target.value as DataField['category'] })}>
                  {DATA_CATEGORIES.map((c) => <option key={c} value={c}>{DATA_CATEGORY_LABELS[c]}</option>)}
                </select>
              </div>
            </div>
            <div className="np-row2">
              <fieldset className="mb-0">
                <legend className="np-field-caption">Personal data?</legend>
                <div className="np-pill-toggle-group">
                  {DATA_SENSITIVITY_ANSWERS.map((answer) => (
                    <button
                      key={answer}
                      type="button"
                      className={`np-pill-toggle${row.isPersonalData === answer ? ` active np-tone-${DATA_SENSITIVITY_TONE[answer]}` : ''}`}
                      aria-pressed={row.isPersonalData === answer}
                      onClick={() => dataRows.patch(row.id, answer === 'no'
                        ? { isPersonalData: answer, isSpecialCategory: false }
                        : { isPersonalData: answer })}
                    >
                      {DATA_SENSITIVITY_LABELS[answer]}
                    </button>
                  ))}
                </div>
                {DATA_SENSITIVITY_HINT[row.isPersonalData] && (
                  <p className="np-hint mt-1 mb-0">{DATA_SENSITIVITY_HINT[row.isPersonalData]}</p>
                )}
              </fieldset>
              <div className="np-field">
                <label>Sensitivity</label>
                <select className="np-select" value={row.sensitivity}
                  onChange={(e) => dataRows.patch(row.id, { sensitivity: e.target.value as DataField['sensitivity'] })}>
                  {SENSITIVITY_LEVELS.map((s) => <option key={s} value={s}>{SENSITIVITY_LABELS[s]}</option>)}
                </select>
              </div>
              {row.isPersonalData !== 'no' && (
                <fieldset className="mb-0">
                  <legend className="np-field-caption">Special category (Article 9)?</legend>
                  <div className="d-flex align-items-center gap-2">
                    <label className="np-switch mb-0" htmlFor={`sub-special-${row.id}`}>
                      <input type="checkbox" id={`sub-special-${row.id}`} checked={row.isSpecialCategory}
                        onChange={(e) => dataRows.patch(row.id, { isSpecialCategory: e.target.checked })} />
                      <span className="np-switch-track"></span>
                      <span className="np-switch-thumb"></span>
                    </label>
                    <label htmlFor={`sub-special-${row.id}`} className="mb-0 small">
                      Special category <span className="np-sub">(health, biometric…)</span>
                    </label>
                  </div>
                </fieldset>
              )}
            </div>
          </RepeatableRowCard>
        ))}
        <button className="np-btn np-btn-ghost np-btn-sm" onClick={() => dataRows.add()} disabled={!dataRows.canAdd}>
          <i className="ri-add-line"></i>Add a data type
        </button>

        <hr className="my-3" />
        <div className="np-row2">
          <div className="np-field">
            <label className="d-flex align-items-center gap-2">
              <input type="checkbox" checked={draft.includesCustomerData}
                onChange={(e) => update({ includesCustomerData: e.target.checked })} />
              Includes customer data
            </label>
          </div>
          <div className="np-field">
            <label className="d-flex align-items-center gap-2">
              <input type="checkbox" checked={draft.includesEmployeeData}
                onChange={(e) => update({ includesEmployeeData: e.target.checked })} />
              Includes employee data
            </label>
          </div>
        </div>
        <div className="np-field">
          <label>Location of processing</label>
          <input className="np-input" placeholder="e.g. Frankfurt, Germany" value={draft.locationOfProcessing}
            onChange={(e) => update({ locationOfProcessing: e.target.value })} />
        </div>
        <div className="np-field">
          <label>Description of processing</label>
          <textarea className="np-input" rows={3} value={draft.descriptionOfProcessing}
            onChange={(e) => update({ descriptionOfProcessing: e.target.value })} />
        </div>
      </SectionCard>

      <SectionCard title="International transfers" icon="ri-global-line">
        <div className="np-field">
          <label className="d-flex align-items-center gap-2">
            <input type="checkbox" checked={draft.transfersOutsideEea}
              onChange={(e) => update({ transfersOutsideEea: e.target.checked })} />
            Transfers personal data outside the EEA
          </label>
        </div>
        {draft.transfersOutsideEea && (
          <>
            <div className="np-row2">
              <div className="np-field">
                <label>Transfer mechanism</label>
                <select className="np-select" value={draft.transferMechanism}
                  onChange={(e) => update({ transferMechanism: e.target.value })}>
                  {TRANSFER_MECHANISMS.map((m) => <option key={m} value={m}>{TRANSFER_MECHANISM_LABELS[m]}</option>)}
                </select>
                {draft.transferMechanism === 'none' && (
                  <p className="np-hint mt-1 mb-0" style={{ color: `var(--bs-${TRANSFER_MECHANISM_TONE.none === 'danger' ? 'danger' : 'warning'})` }}>
                    No lawful Chapter V mechanism recorded for this transfer.
                  </p>
                )}
              </div>
              <div className="np-field">
                <label>Transfers to</label>
                <input className="np-input" placeholder="e.g. USA" value={draft.transferDestination}
                  onChange={(e) => update({ transferDestination: e.target.value })} />
              </div>
            </div>
            <div className="np-field">
              <label className="d-flex align-items-center gap-2">
                <input type="checkbox" checked={draft.transferPreApproved}
                  onChange={(e) => update({ transferPreApproved: e.target.checked })} />
                Pre-approved for this kind of transfer
              </label>
            </div>
          </>
        )}
      </SectionCard>

      <SectionCard title="Data Processing Agreement" icon="ri-file-shield-2-line">
        <div className="np-row2">
          <div className="np-field">
            <label>DPA in place?</label>
            <select className="np-select" value={draft.dpaInPlace}
              onChange={(e) => update({ dpaInPlace: e.target.value as Subprocessor['dpaInPlace'] })}>
              {TRI_STATE.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div className="np-field">
            <label>DPA link</label>
            <input type="url" className="np-input" placeholder="https://…" value={draft.dpaUrl}
              onChange={(e) => update({ dpaUrl: e.target.value })} />
          </div>
        </div>
        <hr className="my-3" />
        <p className="np-field-caption mb-2">Or attach the signed agreement directly</p>
        <DocumentManager entityId={draft.subprocessorId} entityType="subprocessor" />
      </SectionCard>

      <SectionCard title="Review & notes" icon="ri-calendar-check-line">
        <div className="np-field">
          <label>Next review</label>
          <input type="date" className="np-input" value={draft.nextReviewDate}
            onChange={(e) => update({ nextReviewDate: e.target.value })} />
        </div>
        <div className="np-field">
          <label>Last review</label>
          <input type="date" className="np-input" value={draft.lastReviewDate}
            onChange={(e) => update({ lastReviewDate: e.target.value })} />
        </div>
        <div className="np-field">
          <label>Notes</label>
          <textarea className="np-input" rows={3} value={draft.notes}
            onChange={(e) => update({ notes: e.target.value })} />
        </div>
      </SectionCard>

      <SectionCard title="Linked Artefacts" icon="ri-links-line" hint="Link risks, controls, or other artefacts to this vendor">
        <LinkedArtefacts
          artefactId={draft.subprocessorId}
          artefactType="subprocessor"
          artefactRegister="subprocessors"
        />
      </SectionCard>
    </div>
  );
};

// ─── Route wrapper ──────────────────────────────────────────────────────────────

export const SubprocessorDetail: React.FC = () => {
  const { subprocessorId } = useParams<{ subprocessorId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const isNew = subprocessorId === 'new';
  const createStarted = useRef(false);

  const { data, isLoading, isError } = useQuery<Subprocessor>({
    queryKey: ['subprocessor', subprocessorId],
    queryFn: () => subprocessorsApi.getOne(subprocessorId!),
    enabled: !isNew && !!subprocessorId,
  });

  const createMutation = useMutation({
    mutationFn: () => subprocessorsApi.create({ name: '' }),
    onSuccess: (created) => {
      queryClient.setQueryData(['subprocessor', created.subprocessorId], created);
      queryClient.invalidateQueries({ queryKey: ['list', '/subprocessors'] });
      navigate(`/subprocessors/${created.subprocessorId}`, { replace: true });
    },
    onError: (err) => {
      toast.error(describeApiError(err, 'Could not start a new record.'));
      navigate('/subprocessors', { replace: true });
    },
  });

  useEffect(() => {
    if (!isNew || createStarted.current) return;
    createStarted.current = true;
    createMutation.mutate();
  }, [isNew]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isNew || isLoading) {
    return <div className="container-xxl flex-grow-1 container-p-y np-page"><Spinner /></div>;
  }

  if (isError || !data) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y np-page">
        <PageHeader
          icon="ri-shield-keyhole-line"
          title="Record not found"
          subtitle={`Nothing could be loaded for ${subprocessorId ?? 'this reference'}.`}
          breadcrumbs={[{ label: 'Controllers & Processors', to: '/subprocessors' }, { label: 'Not found' }]}
          actions={
            <button className="np-btn np-btn-ghost" onClick={() => navigate('/subprocessors')}>
              <i className="ri-arrow-left-line"></i>Back to register
            </button>
          }
        />
        <div className="np-banner np-tone-danger" role="alert">
          <i className="ri-error-warning-line"></i>
          This record may have been deleted, or you may not have access to it.
        </div>
      </div>
    );
  }

  return <SubprocessorEditor key={data.subprocessorId} record={data} />;
};

export default SubprocessorDetail;
